import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import DashboardLayout from "./DashboardLayout";
import { API_BASE } from "../utils/apiBase";

function ViewExamPage() {
  const overlayStyle = { background: "rgba(0,0,0,.55)", zIndex: 3000 };
  const dialogStyle = { width: "95vw", maxWidth: "1400px", margin: "2vh auto" };
  const dialogMdStyle = { width: "min(860px, 95vw)", maxWidth: "95vw", margin: "2vh auto" };
  const bodyScrollStyle = { maxHeight: "72vh", overflowY: "auto" };
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ models: [], categories: [], courses: [], academy_categories: [], franchises: [] });
  const [loading, setLoading] = useState(true);
  const [viewRow, setViewRow] = useState(null);
  const [editRow, setEditRow] = useState(null);
  const [assignRow, setAssignRow] = useState(null);
  const [sets, setSets] = useState([]);
  const [selectedSet, setSelectedSet] = useState("");
  const [assignManagerRow, setAssignManagerRow] = useState(null);
  const [assignedSetDetail, setAssignedSetDetail] = useState(null);
  const [assignedLoading, setAssignedLoading] = useState(false);
  const [assignedPageSize, setAssignedPageSize] = useState("10");
  const [assignedPage, setAssignedPage] = useState(1);
  const [supportLoaded, setSupportLoaded] = useState(false);
  const [supportLoading, setSupportLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const listRes = await axios.get(`${API_BASE}/exams-admin`);
      setRows(Array.isArray(listRes.data) ? listRes.data : []);
    } finally {
      setLoading(false);
    }
  };

  const loadSupportData = async () => {
    if (supportLoaded || supportLoading) return;
    setSupportLoading(true);
    try {
      const [metaRes, setsRes] = await Promise.all([
        axios.get(`${API_BASE}/exams-admin/meta`),
        axios.get(`${API_BASE}/question-modules`),
      ]);
      setMeta(metaRes.data || {});
      setSets(Array.isArray(setsRes.data) ? setsRes.data : []);
      setSupportLoaded(true);
    } finally {
      setSupportLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const deleteExam = async (id) => {
    const ok = await Swal.fire({ title: "Delete exam?", icon: "warning", showCancelButton: true, confirmButtonText: "Delete" });
    if (!ok.isConfirmed) return;
    await axios.delete(`${API_BASE}/exams-admin/${id}`);
    load();
  };

  const saveEdit = async () => {
    const payload = {
      title: editRow.title,
      academy_category: editRow.academy_category,
      course_model_id: Number(editRow.course_model_id),
      course_category_id: Number(editRow.course_category_id),
      course_id: Number(editRow.course_id),
      exam_date: editRow.exam_date,
      exam_time: editRow.exam_time,
      duration_minutes: Number(editRow.duration_minutes || 60),
      assignment_mode: editRow.assignment_mode || "automatic",
      assigned_franchise_ids: editRow.assigned_franchise_ids || [],
    };
    await axios.post(`${API_BASE}/exams-admin/${editRow.id}`, payload);
    setEditRow(null);
    load();
  };

  const assignSet = async () => {
    if (!selectedSet) return Swal.fire("Required", "Set select korun", "warning");
    await axios.post(`${API_BASE}/exams-admin/${assignRow.id}/assign-question-module`, { question_module_id: Number(selectedSet) });
    setAssignRow(null);
    setSelectedSet("");
    load();
  };

  const generateResult = async (row) => {
    const examDateTime = row?.exam_date && row?.exam_time ? new Date(`${row.exam_date}T${row.exam_time}`) : null;
    if (!examDateTime || Number.isNaN(examDateTime.getTime())) {
      return Swal.fire("Blocked", "Exam date/time invalid. Please set valid date and time first.", "warning");
    }
    if (new Date().getTime() < examDateTime.getTime()) {
      return Swal.fire("Blocked", "Exam time ekhono hoyni. Exam date-time par hole generate korun.", "warning");
    }

    const ok = await Swal.fire({
      title: "Generate Exam Result?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Generate",
    });
    if (!ok.isConfirmed) return;

    try {
      const res = await axios.post(`${API_BASE}/exams-admin/${row.id}/generate-result`);
      Swal.fire("Success", res?.data?.message || "Result generated successfully", "success");
      await load();
    } catch (error) {
      Swal.fire("Failed", error?.response?.data?.message || "Result generate failed", "error");
    }
  };

  const isGenerateEnabled = (row) => {
    if (!row?.exam_date || !row?.exam_time) return false;
    const dt = new Date(`${row.exam_date}T${row.exam_time}`);
    if (Number.isNaN(dt.getTime())) return false;
    return new Date().getTime() >= dt.getTime();
  };

  const openAssignFlow = async (row) => {
    if (row?.question_module_id) {
      setAssignManagerRow(row);
      setAssignedSetDetail(null);
      setAssignedPage(1);
      return;
    }
    if (!supportLoaded) {
      await loadSupportData().catch(() => {});
    }
    setAssignRow(row);
  };

  const deAssignSet = async () => {
    if (!assignManagerRow?.id) return;
    await axios.post(`${API_BASE}/exams-admin/${assignManagerRow.id}/deassign-question-module`);
    Swal.fire("Success", "Question set de-assigned.", "success");
    setAssignManagerRow(null);
    setAssignedSetDetail(null);
    setAssignedPage(1);
    setSelectedSet("");
    setAssignRow(null);
    await load();
  };

  const viewAssignedSet = async () => {
    if (!assignManagerRow?.question_module_id) return;
    setAssignedLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/question-modules/${assignManagerRow.question_module_id}?_ts=${Date.now()}`);
      setAssignedSetDetail(res.data || null);
      setAssignedPage(1);
    } catch (e) {
      Swal.fire("Failed", e?.response?.data?.message || "Assigned set load failed", "error");
    } finally {
      setAssignedLoading(false);
    }
  };

  const sortedSets = useMemo(
    () => [...sets].sort((a, b) => String(a.set_name || "").localeCompare(String(b.set_name || ""))),
    [sets]
  );

  const filteredSets = useMemo(() => {
    if (!assignRow) return [];
    const exactCourseSets = sortedSets.filter((s) => String(s.course_id) === String(assignRow.course_id));
    // Fallback: jodi exact course match na thake, tahole sob set dekhao jate assign block na hoy.
    return exactCourseSets.length > 0 ? exactCourseSets : sortedSets;
  }, [sortedSets, assignRow]);

  const assignedQuestions = useMemo(() => assignedSetDetail?.questions || [], [assignedSetDetail]);
  const assignedPageSizeNumber = useMemo(() => (assignedPageSize === "all" ? (assignedQuestions.length || 1) : Number(assignedPageSize || 10)), [assignedPageSize, assignedQuestions.length]);
  const assignedTotalPages = useMemo(() => Math.max(1, Math.ceil((assignedQuestions.length || 0) / assignedPageSizeNumber)), [assignedQuestions.length, assignedPageSizeNumber]);
  const assignedPaginatedRows = useMemo(() => {
    const start = (assignedPage - 1) * assignedPageSizeNumber;
    return assignedQuestions.slice(start, start + assignedPageSizeNumber);
  }, [assignedQuestions, assignedPage, assignedPageSizeNumber]);

  useEffect(() => {
    if (assignedPage > assignedTotalPages) {
      setAssignedPage(assignedTotalPages);
    }
  }, [assignedPage, assignedTotalPages]);

  useEffect(() => {
    const anyModalOpen = Boolean(viewRow || editRow || assignRow || assignManagerRow);
    if (anyModalOpen) {
      const prevOverflow = document.body.style.overflow;
      const prevOverflowX = document.body.style.overflowX;
      document.body.style.overflow = "hidden";
      document.body.style.overflowX = "hidden";
      return () => {
        document.body.style.overflow = prevOverflow;
        document.body.style.overflowX = prevOverflowX;
      };
    }
    return undefined;
  }, [viewRow, editRow, assignRow, assignManagerRow]);

  return (
    <DashboardLayout>
      <div className="container-fluid">
        <div className="card border-0 shadow-sm">
          <div className="card-body">
            <h3 className="mb-2">View Exam</h3>
            <div className="table-responsive">
              <table className="table table-bordered align-middle">
                <thead>
                  <tr>
                    <th>Sl No</th>
                    <th>Exam Name</th>
                    <th>Course</th>
                    <th>Date</th>
                    <th>Time</th>
                    <th>Set Marks</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan="7">Loading...</td></tr>
                  ) : rows.length === 0 ? (
                    <tr><td colSpan="7">No exam found.</td></tr>
                  ) : rows.map((r, idx) => (
                    <tr key={r.id}>
                      <td>{idx + 1}</td>
                      <td>{r.title}</td>
                      <td>{r.course_name || "-"}</td>
                      <td>{r.exam_date || "-"}</td>
                      <td>{r.exam_time || "-"}</td>
                      <td>{Number(r.question_set_total_marks || 0)}</td>
                      <td style={{ minWidth: 470 }}>
                        <div className="d-flex gap-2 flex-wrap">
                          <button className="btn btn-sm btn-info text-white" onClick={() => setViewRow(r)}>View</button>
                          <button className="btn btn-sm btn-primary" onClick={() => { if (!supportLoaded) loadSupportData().catch(() => {}); setEditRow({ ...r, assigned_franchise_ids: r.assigned_franchise_ids || [] }); }}>Edit</button>
                          <button
                            className={`btn btn-sm ${r?.question_module_id ? "btn-success" : "btn-warning"}`}
                            onClick={() => openAssignFlow(r)}
                          >
                            {r?.question_module_id ? "Already Assign" : "Question Assign"}
                          </button>
                          <button
                            className={`btn btn-sm ${Number(r.generated_result_count || 0) > 0 ? "btn-success" : "btn-secondary"}`}
                            onClick={() => generateResult(r)}
                            disabled={!isGenerateEnabled(r)}
                            title="Click kore exam result generate korun"
                          >
                            Generate Result
                          </button>
                          <button className="btn btn-sm btn-danger" onClick={() => deleteExam(r.id)}>Delete</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {viewRow ? (
        <div className="modal d-block" style={overlayStyle} onClick={() => setViewRow(null)}>
          <div className="modal-dialog modal-dialog-centered" style={dialogMdStyle} onClick={(e) => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header"><h5 className="modal-title">Exam Details</h5><button className="btn-close" onClick={() => setViewRow(null)} /></div>
              <div className="modal-body" style={bodyScrollStyle}>
                <p><b>Exam Name:</b> {viewRow.title}</p>
                <p><b>Academy:</b> {viewRow.academy_category || "-"}</p>
                <p><b>Course:</b> {viewRow.course_name || "-"}</p>
                <p><b>Date/Time:</b> {viewRow.exam_date || "-"} {viewRow.exam_time || ""}</p>
                <p><b>Question Set:</b> {viewRow.question_set_name || "Not assigned"}</p>
                <p><b>Set Total Marks:</b> {Number(viewRow.question_set_total_marks || 0)}</p>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {editRow ? (
        <div className="modal d-block" style={overlayStyle} onClick={() => setEditRow(null)}>
          <div className="modal-dialog modal-dialog-centered" style={dialogMdStyle} onClick={(e) => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header"><h5 className="modal-title">Edit Exam</h5><button className="btn-close" onClick={() => setEditRow(null)} /></div>
              <div className="modal-body" style={bodyScrollStyle}>
                <div className="row g-2">
                  <div className="col-md-6"><label className="form-label">Exam Name</label><input className="form-control" value={editRow.title || ""} onChange={(e) => setEditRow((p) => ({ ...p, title: e.target.value }))} /></div>
                  <div className="col-md-3"><label className="form-label">Date</label><input type="date" className="form-control" value={editRow.exam_date || ""} onChange={(e) => setEditRow((p) => ({ ...p, exam_date: e.target.value }))} /></div>
                  <div className="col-md-3"><label className="form-label">Time</label><input type="time" className="form-control" value={editRow.exam_time || ""} onChange={(e) => setEditRow((p) => ({ ...p, exam_time: e.target.value }))} /></div>
                </div>
              </div>
              <div className="modal-footer"><button className="btn btn-secondary" onClick={() => setEditRow(null)}>Close</button><button className="btn btn-success" onClick={saveEdit}>Save</button></div>
            </div>
          </div>
        </div>
      ) : null}

      {assignRow ? (
        <div className="modal d-block" style={overlayStyle}>
          <div className="modal-dialog modal-dialog-centered" style={{ width: "min(620px, 95vw)", margin: "2vh auto" }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header"><h5 className="modal-title">Question Assign</h5><button className="btn-close" onClick={() => setAssignRow(null)} /></div>
              <div className="modal-body" style={bodyScrollStyle}>
                <label className="form-label">Select Question Set</label>
                <select
                  className="form-select"
                  value={selectedSet}
                  onChange={(e) => setSelectedSet(e.target.value)}
                  disabled={supportLoading}
                >
                  <option value="">{supportLoading ? "Loading sets..." : "Select set"}</option>
                  {filteredSets.map((s) => (
                    <option key={s.id} value={s.id}>
                      {`${s.set_name || "Untitled Set"} | ${s.module_title || "No Module"} | Marks: ${Number(s.total_marks || 0)}`}
                    </option>
                  ))}
                </select>
                {!supportLoading && filteredSets.length === 0 ? (
                  <div className="small text-danger mt-1">Ei course-er jonne kono question set paoa jayni.</div>
                ) : null}
              </div>
              <div className="modal-footer"><button className="btn btn-secondary" onClick={() => setAssignRow(null)}>Close</button><button className="btn btn-warning" onClick={assignSet}>Assign</button></div>
            </div>
          </div>
        </div>
      ) : null}

      {assignManagerRow ? (
        <div className="modal d-block" style={overlayStyle}>
          <div className="modal-dialog modal-dialog-centered" style={{ ...dialogStyle, maxWidth: "none" }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Assigned Question Set Controls</h5>
                <button className="btn-close" onClick={() => { setAssignManagerRow(null); setAssignedSetDetail(null); setAssignedPage(1); }} />
              </div>
              <div className="modal-body" style={{ maxHeight: "76vh", overflowY: "auto", overflowX: "hidden" }}>
                <div className="mb-3">
                  <div><b>Exam:</b> {assignManagerRow.title}</div>
                  <div><b>Assigned Set:</b> {assignManagerRow.question_set_name || "Unknown"}</div>
                </div>
                <div className="alert alert-warning py-2 mb-3">
                  <b>Question Already Assign</b>
                </div>

                <div className="d-flex flex-wrap gap-2 mb-3">
                  <button className="btn btn-danger btn-sm" onClick={deAssignSet}>De-Assign</button>
                  <button className="btn btn-primary btn-sm" onClick={viewAssignedSet}>View Assign</button>
                </div>

                {assignedLoading ? <div>Loading assigned set...</div> : null}

                {assignedSetDetail ? (
                  <div className="card border-0 shadow-sm">
                    <div className="card-body">
                      <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
                        <h6 className="mb-0">
                          Set: {assignedSetDetail?.module?.set_name}{" "}
                          <span className="badge bg-primary ms-2">Total Marks: {Number(assignedSetDetail?.total_marks || 0)}</span>
                        </h6>
                        <div className="d-flex align-items-center gap-2">
                          <select className="form-select form-select-sm" style={{ width: 110 }} value={assignedPageSize} onChange={(e) => { setAssignedPageSize(e.target.value); setAssignedPage(1); }}>
                            <option value="10">10</option>
                            <option value="25">25</option>
                            <option value="50">50</option>
                            <option value="100">100</option>
                            <option value="all">All</option>
                          </select>
                          <button className="btn btn-sm btn-outline-secondary" disabled={assignedPage <= 1} onClick={() => setAssignedPage((p) => Math.max(1, p - 1))}>Prev</button>
                          <span className="small">Page {assignedPage} / {assignedTotalPages}</span>
                          <button className="btn btn-sm btn-outline-secondary" disabled={assignedPage >= assignedTotalPages} onClick={() => setAssignedPage((p) => Math.min(assignedTotalPages, p + 1))}>Next</button>
                        </div>
                      </div>

                      <div style={{ overflowX: "hidden" }}>
                        <table className="table table-bordered align-middle mb-0" style={{ width: "100%", tableLayout: "fixed" }}>
                          <thead>
                            <tr>
                              <th style={{ width: 70 }}>Sl No</th>
                              <th style={{ width: 150 }}>Type</th>
                              <th>Question</th>
                              <th>Option</th>
                              <th style={{ width: 90 }}>Marks</th>
                            </tr>
                          </thead>
                          <tbody>
                            {assignedPaginatedRows.map((q, idx) => (
                              <tr key={q.id || idx}>
                                <td style={{ whiteSpace: "nowrap" }}>{((assignedPage - 1) * assignedPageSizeNumber) + idx + 1}</td>
                                <td style={{ whiteSpace: "normal", wordBreak: "break-word" }}>{q.question_type || "-"}</td>
                                <td style={{ whiteSpace: "normal", wordBreak: "break-word" }}>{q.question || "-"}</td>
                                <td>
                                  <div style={{ border: "1px solid #e2e8f0", borderRadius: 8, padding: "6px 8px", background: "#f8fafc" }}>
                                    {[q.option_a, q.option_b, q.option_c, q.option_d].filter(Boolean).map((opt, optIdx) => (
                                      <div key={optIdx} style={{ whiteSpace: "normal", wordBreak: "break-word" }}><b>{String.fromCharCode(65 + optIdx)}.</b> {opt}</div>
                                    ))}
                                  </div>
                                </td>
                                <td style={{ whiteSpace: "nowrap" }}>{Number(q.marks ?? 1)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => { setAssignManagerRow(null); setAssignedSetDetail(null); setAssignedPage(1); }}>Close</button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </DashboardLayout>
  );
}

export default ViewExamPage;
